import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PatientBaseCaregiverResponse, PatientControllerv2Service } from '@app/generated-api';
import { CardComponent, CardViewModeContentDirective } from '@components/card/card.component';
import {
    MdsButtonComponent,
    MdsExpansionPanelComponent,
    MdsTagComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { AllPatientStatusesEnum } from '@services/patient-status/patient-status.service';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import {
    CaregiverDialogComponent,
    CaregiverDialogData,
    CaregiverDialogResult,
} from '../caregiver-info-card/caregiver-dialog/caregiver-dialog.component';
import { LabeledTextComponent } from '../labeled-text/labeled-text.component';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';

@Component({
    selector: 'medpace-request-personal-info-card',
    imports: [
        LabeledTextComponent,
        MdsExpansionPanelComponent,
        MdsTagComponent,
        MdsButtonComponent,
        CardComponent,
        CardViewModeContentDirective,
    ],
    templateUrl: './request-personal-info-card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestPersonalInfoCardComponent {
    readonly patientId = input.required<number | undefined>();
    readonly studyId = input.required<number | undefined>();
    private readonly patientService = inject(PatientControllerv2Service);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly userService = inject(UserServiceV2);
    readonly canViewPcsId = this.userService.signalPermissions.patient.pcsIdView;
    patientResource = rxResource({
        params: () => {
            const patientId = this.patientId();

            if (patientId === undefined) return undefined;
            return {
                patientId,
            };
        },
        stream: ({ params }) => {
            return this.patientService.patientControllerv2GetPatientDetailedById(params.patientId);
        },
    });

    patientName = computed(() => {
        const patient = this.patientResource.value();
        if (!patient) return null;
        if (patient.statusId === AllPatientStatusesEnum.Removed) return 'Removed';
        return `${patient.personalInfo.firstName} ${patient.personalInfo.lastName}`;
    });

    isMinor = computed(() => this.patientResource.value()?.personalInfo.isMinor ?? false);

    caregivers = computed(() => this.patientResource.value()?.caregivers.caregivers ?? []);

    primaryCaregiver = computed(() => this.caregivers().find((c) => c.isPrimary));

    otherCaregivers = computed(() => this.caregivers().filter((c) => !c.isPrimary));

    isPatientRemoved = computed(() => this.patientResource.value()?.statusId === AllPatientStatusesEnum.Removed);

    profileLinkClicked(): void {
        const patientId = this.patientId();
        const studyId = this.studyId();
        const siteId = this.patientResource.value()?.siteId;

        if (patientId === undefined || studyId === undefined || siteId === undefined) return;

        const url = this.router.serializeUrl(
            this.router.createUrlTree(['/studies', studyId, 'sites', siteId, 'patients', patientId, 'edit'])
        );
        window.open(url, '_blank');
    }

    activityLinkClicked(): void {
        const patientId = this.patientId();
        const studyId = this.studyId();
        const siteId = this.patientResource.value()?.siteId;

        if (patientId === undefined || studyId === undefined || siteId === undefined) return;

        const url = this.router.serializeUrl(
            this.router.createUrlTree(['/studies', studyId, 'sites', siteId, 'patients', patientId, 'overview'])
        );
        window.open(url, '_blank');
    }

    onViewCaregiver(model: PatientBaseCaregiverResponse): void {
        this.dialog.open<CaregiverDialogComponent, CaregiverDialogData, CaregiverDialogResult>(
            CaregiverDialogComponent,
            {
                data: {
                    mode: 'view',
                    model,
                },
                ...getModalConfig(ModalSize.Medium),
            }
        );
    }

    getCaregiverFullName(caregiver: PatientBaseCaregiverResponse): string {
        return [caregiver.firstName, caregiver.middleName, caregiver.lastName].filter(Boolean).join(' ');
    }
}
